<template>
  <div class="text-xs-left drug-prod-select">
    <label aria-hidden="true" class="vue-treeselect-label v-label v-label--active theme--light has-value truncate pr-3 my-1" :class="value && value.length > 0 ? 'has-value' : ''" v-if="label">
      {{ label }}
    </label>
    <v-treeselect
      ref="drugSelect"
      class="hvh-flex align-center grow-1"
      :value="value"
      v-on:input="updateValue"
      :instance-id="instanceId"
      open-direction="below"
      :multiple="!singleSelect"
      :show-count="true"
      :default-options="true"
      :async="true"
      :loading-text="loadingText"
      :load-options="handleTreeselectEvent.bind(_self, value)"
      :disabled="readonly"
      :noResultsText="noResultsText"
      :cache-options="false"
      :flat="!singleSelect"
      :normalizer="normalizer"
      :appendToBody="true"
      @search-change="searchChange"
      :placeholder="placeholder || 'Start typing to search...'"
    >
      <TreeSelectItem slot="value-label" slot-scope="{ node }" :node="node" :eventHashMap="eventHashMap" :singleSelect="singleSelect" @mousedown="mousedownValue($event)" />
      <label slot="option-label" slot-scope="{ node, shouldShowCount, count, labelClassName, countClassName }" :class="labelClassName">
        <div class="cohort-select" v-if="node.raw.cohort">
          <v-list-tile-content class="cohort-select-list__tile__content">
            <v-list-tile-title>{{ node.raw.cohort.name }}</v-list-tile-title>
            <v-list-tile-sub-title style="display: flex; flex-wrap: wrap">
              <div class="mr-3"><b>Created:</b> {{ node.raw.cohort.createdOn | formatDate }}</div>
              <div class="mr-3"><b>Patients:</b> {{ (node.raw.cohort.totalCount != null ? node.raw.cohort.totalCount : 'N/A') | formatNumber }}</div>
              <div class="mr-3"><b>Creator:</b> {{ node.raw.cohort.createdBy ? node.raw.cohort.createdBy : 'N/A' }}</div>
            </v-list-tile-sub-title>
          </v-list-tile-content>
        </div>
        <div v-else>
          <span :title="node.raw.fullDescription" v-html="highlightTerm(searchTerm, node ? node.label : '')"></span>
          <span v-if="eventHashMap[node.id] && eventHashMap[node.id].exampleBrand" class="grey--text" :title="eventHashMap[node.id].exampleBrand"> (e.g. <span v-html="highlightTerm(searchTerm, eventHashMap[node.id].exampleBrand)"></span>) </span>
          <span v-if="shouldShowCount && node.childrenStates.isLoaded" :class="countClassName">({{ count }})</span>
        </div>
      </label>
    </v-treeselect>
  </div>
</template>

<script>
import VTreeselect from '@riophae/vue-treeselect';
import axios from '../../../axios';
import { Debouncer } from '../../../libraries/common';
import { DEFAULT_LOADING_TEXT, hashMapAddWithKey, hashMapClearAll, MIN_SEARCH_LENGTH, STR_SPLITTER, TYPING_TIMEOUT } from '../../../libraries/dpdSearch';
import TreeSelectItem from './TreeSelectItem.vue';

export default {
  components: { VTreeselect, TreeSelectItem },
  props: {
    value: [String, Array],
    events: Object,
    instanceId: String,
    eventSearchUrl: String,
    childrenSearchUrl: String,
    cohortSelectorItems: Array,
    tagSelectorItems: Array,
    defaultTreeElements: Array,
    categorySetting: {
      type: Object,
      default: null
    },
    label: {
      type: String,
      default: 'USC, Drugs, Procedures, Diagnoses, Cohorts'
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    singleSelect: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      options: null,
      loadingText: '',
      eventHashMap: this._props.events || {},
      searchTerm: '',
      lastSearch: undefined,
      noResultsText: 'No results found...',
      debouncer: new Debouncer(TYPING_TIMEOUT),
    };
  },
  methods: {
    searchChange(term) {
      this.searchTerm = term;
      if (this.singleSelect) {
        this.$refs.drugSelect.menu = { ...this.$refs.drugSelect.menu, current: '' };
      }
    },

    highlightTerm(term, text) {
      try {
        if (term.length && text) {
          let regex = new RegExp(term.replace(/([.?*+^$[\]\\(){}|-])/g, "\\$1"), 'gi');
          return text.replace(regex, '<b>$&</b>');
        }
        else {
          return text;
        }
      }
      catch (e) {
        console.log(e.message);
        return text;
      }
    },
    updateValue: function (value) {
      this.$emit('input', value || (this.singleSelect && this.lastSearch));

      if (this.events) {
        var eventsClone = Object.assign(Object.create(Object.getPrototypeOf(this.events)), this.events);
        if (eventsClone.clearAll) eventsClone.clearAll();
        this.$emit('update:events', Object.assign(eventsClone, hashMapClearAll(this.eventHashMap, value)));
      }

      if (this.singleSelect) {
        setTimeout(() => this.lastSearch = undefined, 100);
      }
    },
    clear() {
      this.lastSearch = undefined;
      this.$refs.drugSelect.clear();
    },
    getNode(id) {
      return this.$refs.drugSelect.getNode(id);
    },
    mousedownValue: function (event) {
      const itemText = event.target.innerText
      let text, icd;
      [text, icd] = this.getSearchValues(itemText)
      this.customHandleRemoteSearch(icd ? icd : text)
    },

    customHandleRemoteSearch(searchQuery) {
      this.loadingText = DEFAULT_LOADING_TEXT;
      this.noResultsText = "No results found..."

      const drugSelect = this.$refs.drugSelect

      drugSelect.trigger.searchQuery = ''
      if (drugSelect.remoteSearchQuery) {
        drugSelect.remoteSearchQuery[drugSelect.trigger.searchQuery] = null
      }
      const entry = drugSelect.getRemoteSearchEntry()

      const done = () => {
        drugSelect.initialize()
        drugSelect.resetHighlightedOptionWhenNecessary(true)
      }

      if ((searchQuery === '' || drugSelect.cacheOptions) && entry.isLoaded) {
        return done()
      }

      drugSelect.callLoadOptionsProp({
        action: "ASYNC_SEARCH",
        args: { searchQuery },
        isPending() {
          return entry.isLoading
        },
        start: () => {
          entry.isLoading = true
          entry.isLoaded = false
          entry.loadingError = ''
          drugSelect.openMenu()
        },
        succeed: options => {
          entry.isLoaded = true
          entry.options = options
          this.searchTerm = searchQuery
          // When the request completes, the search query may have changed.
          // We only show these options if they are for the current search query.
          if (drugSelect.trigger.searchQuery === '') done()
        },
        fail: err => {
          entry.loadingError = err.message || String(err)
        },
        end: () => {
          entry.isLoading = false
        },
      })
    },

    handleTreeselectEvent(criterion, event) {
      if (event.action === "ASYNC_SEARCH") {
        const isCodeString = event.searchQuery.match(/^[A-z]{3}(-|\d)/i);
        if (isCodeString && event.searchQuery.length < MIN_SEARCH_LENGTH + 4) {
          event.callback(null, []);
          this.noResultsText = "Type at least 8 characters to search by code";
          return;
        } else if (event.searchQuery.length < MIN_SEARCH_LENGTH) {
          event.callback(null, []);
          this.noResultsText = "Type at least 4 characters to search by keyword";
          return;
        } else {
          this.loadingText = DEFAULT_LOADING_TEXT;
          this.noResultsText = "No results found...";
        }
        this.loadOptions(event, criterion);
      }
      if (event.action === "LOAD_CHILDREN_OPTIONS") {
        this.loadChildren(event)
      }
    },
    addToHashMapWithKey(key, obj) {
      this.eventHashMap = hashMapAddWithKey(this.eventHashMap, key, obj);
      return key;
    },
    populateHashMap(events) {
      let noChildrenTypes = ['dxname', 'drugndc', 'procdescrip'];
      let mapChildren = function (roots, mapFunction) {
        let result = (roots || []).map(c => {
          let obj = mapFunction(c);
          if (c.children)
            obj.children = mapChildren(c.children, mapFunction)
          else if (noChildrenTypes.indexOf(c.type.toLowerCase()) != -1)
            obj.children = undefined;
          return obj
        });
        return result;
      }

      var classChildren = (events.classes || [])
        .map(drugClass => ({
          id: this.addToHashMapWithKey(drugClass.type + "/" + drugClass.id, drugClass),
          label: drugClass.label,
          children: null
        }));
      //usc
      var uscChildren = (events.classes || []).map(uscClass => ({
        id: this.eventHashMap.addWithKey(uscClass.type + "/" + uscClass.id, uscClass),
        label: uscClass.label,
        children: null
      }));

      var detailChildren = this.parseChildNodes(events.details, null);

      var procChildren = mapChildren(events.procedures, proc => ({
        id: this.addToHashMapWithKey(proc.type + "/" + proc.id, proc),
        label: proc.label,
        fullDescription: proc.fullDescrip
      }));

      var procClassChildren = mapChildren(events.procClass, procClass => ({
        id: this.addToHashMapWithKey(procClass.type + "/" + procClass.id, procClass),
        label: procClass.label,
        children: null
      }));

      var diagnosisChildren = mapChildren(events.diagnosisGroup, group => ({
        id: this.addToHashMapWithKey(group.type + "/" + group.id, group),
        label: group.label,
        children: null
      }));

      var physicianTypeChildren = (events.physicianType || []).map(physicianType => ({
        id: this.addToHashMapWithKey(physicianType.type + "/" + physicianType.id, physicianType),
        label: physicianType.label,
        children: undefined
      }));
      const disabledLabel = "< Results limited to 100 - Refine search query to see more>";
      if (procChildren.length == 100) {
        procChildren = [...procChildren, {
          id: "topProcLabel",
          label: disabledLabel,
          children: undefined,
          isDisabled: true
        }]
      }
      if (diagnosisChildren.length == 100) {
        diagnosisChildren = [...diagnosisChildren, {
          id: "topDiagLabel",
          label: disabledLabel,
          children: undefined,
          isDisabled: true
        }]
      }
      return {
        classChildren,
        detailChildren,
        procChildren,
        procClassChildren,
        diagnosisChildren,
        physicianTypeChildren,
        uscChildren,
      }
    },


    getEventId(obj) {
      if (!obj.type) return obj.id;

      var translator = {
        drugstrength: obj.strength,
        drugndc: obj.ndcnum,
        drugform: obj.form,
        drugbrand: obj.brand,
      };

      let key = obj.type.toLowerCase();
      let val = translator[key] || obj.id;
      return `${key}/${val}`;
    },
    parseNodeDetails(target, source) {
      if (source.type) {
        if (['drugndc', 'dxname'].indexOf(source.type.toLowerCase()) != -1) {
          target.children = undefined;
        }
      }
      return target;
    },

    parseChildNodes(children, parentId) {
      if (children == undefined) return null;
      if (children.length == 0) return [];
      let childNodes = children.map(child => {
        let newObjId = this.addToHashMapWithKey((parentId != null ? parentId + "/" : "") + this.getEventId(child), child);
        let obj = {
          id: newObjId,
          label: child.label,
          type: child.type,
          children: this.parseChildNodes(child.children, newObjId)
        }

        this.parseNodeDetails(obj, child);
        return obj;
      });

      return childNodes;
    },

    loadEventsForSingleQuery(event) {
      var query = event.searchQuery;

      var getParams = {
        params: null,
        timeout: 60000
      };

      if (this.categorySetting) {
        getParams.params = {
          query: query,
          Drug: this.categorySetting.drug,
          Diagnose: this.categorySetting.diagnose,
          Procedure: this.categorySetting.procedure,
          Cohort: this.categorySetting.cohort,
          Physician: this.categorySetting.physician
        };
      } else {
        getParams.params = { query: query };
      }

      axios.get(this.eventSearchUrl, getParams)
        .then(response => {
          const query = getParams.params.query;

          const {
            classChildren,
            detailChildren,
            procChildren,
            procClassChildren,
            diagnosisChildren,
            physicianTypeChildren,
            uscChildren,
          } = this.populateHashMap(response.data);

          var cohortChildren = (this.cohortSelectorItems || [])
            .map(cohort => ({
              id: cohort.id,
              label: "Cohort: " + cohort.name,
              type: "cohort",
              patientDatasource: cohort.patientDatasource,
              cohort: cohort.cohort,
            }))
            .filter(cohort => cohort.label.toLowerCase().indexOf(query.toLowerCase().trim()) >= 0)
            .map(cohort => ({
              id: this.addToHashMapWithKey(cohort.type + "/" + cohort.id, cohort),
              label: cohort.label,
              fullDescription: cohort.description,
              cohort: cohort.cohort
            }));

          var tagChildren = (this.tagSelectorItems || [])
            .filter(tag => tag.toLowerCase().indexOf(query.toLowerCase().trim()) >= 0)
            .map(tag => ({
              id: this.addToHashMapWithKey(`tag/${tag}`, { label: tag }),
              label: tag,
            }));

          // IDs for root elements have the search append to them. This is so, subsequent searches don't overwrite the selected elements
          //  e.g. We search for Aspirin and select all classes (root:class is added to list). When we search for Ibuprofen, treeselect
          //       will add all new matching classes to selected list
          var drugOptions = [];
          if ((classChildren || []).length > 0) {
            drugOptions.push({
              id: 'classRoot:' + this.joinChildIds(classChildren),
              label: 'Class Name',
              query: query,
              children: classChildren,
              isDefaultExpanded: false
            });
          }
          if ((detailChildren || []).length > 0) {
            drugOptions = drugOptions.concat(detailChildren);
          }

          var procedureOptions = [];
          if ((procClassChildren.length || []) > 0) {
            procedureOptions.push({
              id: 'procClassRoot:' + this.joinChildIds(procClassChildren),
              label: 'Procedure Class',
              query: query,
              isDisabled: true,
              children: procClassChildren,
              isDefaultExpanded: false,
            });
          }
          if ((procChildren.length || []) > 0) {
            procedureOptions.push({
              id: 'procNameRoot:' + this.joinChildIds(procChildren),
              label: 'Procedure Name',
              query: query,
              isDisabled: true,
              children: procChildren,
              isDefaultExpanded: false,
            });
          }

          var allOptions = [];

          if (this.singleSelect) {
            allOptions.push({
              id: 'lastSearch',
              label: this.lastSearch,
            });
          }

          if (drugOptions.length > 0) {
            allOptions.push({
              id: 'drugRoot:' + this.joinChildIds(drugOptions),
              label: 'Drugs',
              query: query,
              isDisabled: true,
              children: drugOptions,
              isDefaultExpanded: false
            });
          }

          if (cohortChildren.length > 0) {
            allOptions.push({
              id: 'cohortRoot:' + this.joinChildIds(cohortChildren),
              label: 'Cohorts',
              query: query,
              isDisabled: true,
              children: cohortChildren,
              isDefaultExpanded: false,
            });
          }

          if (tagChildren.length > 0) {
            allOptions.push({
              id: 'tagRoot:' + this.joinChildIds(tagChildren),
              label: 'Tags',
              query: query,
              isDisabled: true,
              children: tagChildren,
              isDefaultExpanded: false,
            });
          }

          if (procedureOptions.length > 0) {
            allOptions.push({
              id: 'procRoot:' + this.joinChildIds(procedureOptions),
              label: 'Procedures',
              query: query,
              isDisabled: true,
              children: procedureOptions,
              isDefaultExpanded: false,
            });
          }

          if ((diagnosisChildren || []).length > 0) {
            allOptions.push({
              id: 'dxRoot:' + this.joinChildIds(diagnosisChildren),
              label: 'Diagnoses',
              query: query,
              isDisabled: true,
              children: diagnosisChildren,
              isDefaultExpanded: false,
            });
          }

          var physicianOptions = [];
          if ((physicianTypeChildren || []).length > 0) {
            physicianOptions = physicianOptions.concat(physicianTypeChildren);
          }
          if (physicianOptions.length > 0) {
            allOptions.push({
              id: 'physicianType:' + this.joinChildIds(physicianOptions),
              label: 'Physician Types',
              query: query,
              isDisabled: true,
              children: physicianOptions,
              isDefaultExpanded: false,
            });
          }

          // If there's only one root tree, expand it by default
          let expandItems = function (options) {
            if (!options || options.length !== 1) return;
            options[0].isDefaultExpanded = true;
            expandItems(options[0].children);
          }

          expandItems(allOptions);

          // TODO: Update default elements when loading children as well
          // this.defaultTreeElements = allOptions;
          event.callback(null, allOptions);
        })
        .catch(error => {
          console.log(error);
          if (error.response && error.response.status == 400) {
            event.callback(new Error('Failed to load options: ' + error.response.data));
          } else {
            event.callback(new Error('Error occurred while loading the results'));
          }
        });
    },

    loadEventsForMultipleQuery(query) {
      let self = this;
      if (self.value == null)
        self.value = [];
      var getParams = {
        params: null,
        timeout: 60000
      };

      if (this.categorySetting) {
        getParams.params = {
          query: query,
          Drug: this.categorySetting.drug,
          Diagnose: this.categorySetting.diagnose,
          Procedure: this.categorySetting.procedure,
          Cohort: this.categorySetting.cohort,
          Physician: this.categorySetting.physician
        };
      } else {
        getParams.params = { query: query };
      }

      axios.get(this.eventSearchUrl, getParams)
        .then(response => {
          const {
            classChildren,
            detailChildren,
            procChildren,
            procClassChildren,
            diagnosisChildren,
            physicianTypeChildren,
            uscChildren,
          } = this.populateHashMap(response.data);

          const allEvents = [].concat(classChildren, detailChildren, procChildren, procClassChildren, diagnosisChildren, physicianTypeChildren,uscChildren).filter(f => f);
          const findRec = (o, query, icdCode) => {
            let q = query.toLowerCase();
            let icd = icdCode ? icdCode.toLowerCase() : null;
            if (typeof (o) == "object" && (o != null)) {
              for (let k of Object.keys(o)) {
                if (typeof (o[k]) == "object") {
                  let r = findRec(o[k], query, icd)
                  if (r)
                    return r
                }
                else if (k == "label" && o[k].toLowerCase().indexOf(q) > -1 && (!icd || o[k].toLowerCase().indexOf("(" + icd + ")") > 0)) {
                  return o
                }
              }
            }
            return null
          }

          let text, icd;
          [text, icd] = this.getSearchValues(query);
          const evt = findRec(allEvents, text, icd);
          if (evt) {
            self.value.push(evt.id);
          } else {
            self.value.push(query);
          }
        })
        .catch(error => {
          console.log(error);
        });
    },

    loadOptions(event) {
      this.debouncer.clear()
        .push(() => {
          const codes = this.isICDcodes(event.searchQuery);
          // const queries = event.searchQuery.trim().split(',').map(v => v.trim().toLowerCase());
          if (codes === false) {
            this.loadEventsForSingleQuery(event);
          } else {
            codes.forEach(code => {
              this.loadEventsForMultipleQuery(code);
            });

            const drugSelect = this.$refs.drugSelect;
            drugSelect.closeMenu();
            event.callback(null, []);
          }
        });
    },
    loadChildren(event) {
      var parent = this.eventHashMap[event.parentNode.id];
      var loadChildrenParams = {
        params: {
          query: this.searchTerm,
          id: parent.id,
          type: parent.type,
          path: parent.path,
          brand: parent.brand,
          form: parent.form,
          strength: parent.strength,
          chain: parent.chain
        },
        timeout: 60000
      };
      axios.get(this.childrenSearchUrl, loadChildrenParams)
        .then(response => {
          if (response.data.length > 0) {
            // Convert responses into treeselect nodes, and insert objects into HashMap

            let childNodes = this.parseChildNodes(response.data, event.parentNode.id);
            event.parentNode.children = childNodes;
          } else {
            event.parentNode.children = undefined
          }
          event.callback()
        })
        .catch(error => {
          if (error.response && error.response.status === 400) {
            event.callback(new Error('Failed to load options: ' + error.response.data));
          }
          else {
            event.callback(new Error('Error occurred while loading the results'));
          }
          console.log(error);
        });
    },

    joinChildIds(children) {
      return children.filter(t => t['id'] !== 'topProcLabel' && t['id'] !== 'topDiagLabel').map(c => c['id']).join(STR_SPLITTER);
    },

    getSearchValues(label) {
      if (label.toLowerCase().indexOf("ndc ") == 0) {
        return ["", label]
      } else if (label.toLowerCase().indexOf("(icd") > -1) {
        return [label.replace(/(.*)\(([^)]+)*\).*$/g, "$1"), label.replace(/(.*)\(([^)]+)*\).*$/g, "$2")]
      } else {
        if (label.indexOf(" | ") > 0)
          label = label.substr(0, label.indexOf(" | "));
        return [label]
      }
    },

    normalizer(node) {
      if (node.label) {
        return node;
      }
      else {
        return {
          id: node.id,
          label: this.singleSelect ? node.id : `${node.id} *Not Found*`
        };
      }
    },

    isICDcodes(value) {
      const codes = value.trim().split(',').map(v => v.trim().toLowerCase()).filter(v => v.length);
      // const code = codes.find(c => !c.startsWith('icd'))
      // if (code) {
      //   /// All codes are not type of ICD
      //   return false;
      // }
      if (codes.length < 2) {
        return false;
      }
      return codes;
    },
    enterPressed(event) {
      if (this.singleSelect) {
        if (event.code === 'Enter') {
          event.target.blur();
        } else {
          this.lastSearch = event.target.value;
        }
      }
    },
  },
  mounted() {
    if (process.env.NODE_ENV === 'development') window['thisDrugProdSelect'] = this;
    if (this.singleSelect) {
      var inputElement = this.$refs.drugSelect.$el.querySelector('.vue-treeselect__input');
      inputElement.removeEventListener('keyup', this.enterPressed);
      inputElement.addEventListener('keyup', this.enterPressed);
    }
  },

}
</script>

<style lang="scss">
.drug-prod-select {
  display: flex;
  flex-flow: column;
  min-height: 56px;
  border: 1px solid #c7cfd3;
  border-radius: 4px;
  padding: 0px 0 0 16px;

  &.summary {
    border: none;
    padding-left: 0;

    .vue-treeselect--disabled {
      .vue-treeselect__control {
        background-color: transparent;
        font-weight: bold;
      }

      .vue-treeselect__control-arrow-container {
        display: none;
      }
    }
  }

  .vue-treeselect__multi-value-item-container {
    overflow: hidden;

    .vue-treeselect__multi-value-item {
      overflow: hidden;
      display: flex !important;
      height: 100%;

      .vue-treeselect__value-remove {
        margin: auto;
      }

      .vue-treeselect__multi-value-label {
        overflow: hidden;
      }
    }
  }

  .vue-treeselect__placeholder {
    height: fit-content;
    margin: auto;
  }
}
</style>